import React, { useCallback, useEffect, useMemo, useState } from 'react';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import styles from './PriceChangeLogs.module.css';
import {
  fetchPriceChangeLogs,
  type PriceChangeLogRow,
} from './api/priceChangeLogs.api';

const PAGE_SIZE = 50;

const SOURCE_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'admin', label: 'Manual' },
  { value: 'mobile', label: 'Mobile app' },
  { value: 'list_on_marketplace', label: 'List on Marketplace' },
  { value: 'vl_concierge_sync', label: 'Victory Live auto sync' },
  { value: 'vl_email', label: 'VL email / intake' },
];

const CLIENT_TYPE_OPTIONS = [
  { value: '', label: 'All client types' },
  { value: 'self-pricing', label: 'Self Pricing' },
  { value: 'concierge', label: 'Concierge' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'Success', label: 'Success' },
  { value: 'Failure', label: 'Failure' },
];

function formatMoney(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return `$${Number(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatWhen(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function normalizePricingMode(value: string | null | undefined): 'self-pricing' | 'concierge' | '' {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'self-pricing' || raw === 'self pricing') return 'self-pricing';
  if (raw === 'concierge') return 'concierge';
  return '';
}

function formatClientTypeLabel(value: string | null | undefined): string {
  const mode = normalizePricingMode(value);
  if (mode === 'self-pricing') return 'Self Pricing';
  if (mode === 'concierge') return 'Concierge';
  return '—';
}

function clientTypeBadgeClass(value: string | null | undefined): string {
  const mode = normalizePricingMode(value);
  if (mode === 'self-pricing') return styles.badgeSelfPricing;
  if (mode === 'concierge') return styles.badgeConcierge;
  return styles.badgeDefault;
}

function sourceBadgeClass(sourceKey: string): string {
  const key = String(sourceKey || '').toLowerCase();
  if (key === 'admin') return styles.badgeAdmin;
  if (key === 'mobile') return styles.badgeMobile;
  if (key === 'list_on_marketplace') return styles.badgeListMarketplace;
  if (key === 'vl_concierge_sync' || key === 'vl_sync') return styles.badgeVl;
  if (key && !['admin', 'mobile', 'vl_concierge_sync', 'vl_sync', 'admin_audit', 'list_on_marketplace'].includes(key)) {
    return styles.badgeEmail;
  }
  return styles.badgeDefault;
}

function isVlAutoSyncSource(sourceKey: string | null | undefined): boolean {
  const key = String(sourceKey || '').toLowerCase();
  return key === 'vl_concierge_sync' || key === 'vl_sync';
}

function changedByFallback(row: PriceChangeLogRow): string {
  if (isVlAutoSyncSource(row.sourceKey)) return 'Victory Live sync';
  if (row.changedByName) return row.changedByName;
  const key = String(row.sourceKey || '').toLowerCase();
  if (key === 'admin') return 'Manual';
  if (key === 'mobile') return 'App user';
  if (key === 'list_on_marketplace') return 'Admin (List on Marketplace)';
  return 'System';
}

function priceClass(direction: PriceChangeLogRow['direction']): string {
  if (direction === 'up') return styles.priceUp;
  if (direction === 'down') return styles.priceDown;
  return styles.priceSame;
}

function syncStatusBadgeClass(status: PriceChangeLogRow['syncStatus']): string {
  return String(status || '').toLowerCase() === 'failure' ? styles.badgeFailure : styles.badgeSuccess;
}

export type PriceChangeLogsPanelProps = {
  tpId?: string;
  tpIds?: string[];
  compact?: boolean;
  hideFilters?: boolean;
  hideClientColumns?: boolean;
  refreshSignal?: number;
};

const PriceChangeLogsPanel: React.FC<PriceChangeLogsPanelProps> = ({
  tpId,
  tpIds,
  compact = false,
  hideFilters = false,
  hideClientColumns = false,
  refreshSignal = 0,
}) => {
  const [logs, setLogs] = useState<PriceChangeLogRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [source, setSource] = useState('');
  const [clientType, setClientType] = useState('');
  const [syncStatus, setSyncStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const scopedTpIds = useMemo(() => {
    const ids = [
      ...(Array.isArray(tpIds) ? tpIds : []),
      ...(tpId ? [tpId] : []),
    ]
      .map((id) => String(id || '').trim())
      .filter(Boolean);
    return [...new Set(ids)];
  }, [tpId, tpIds]);

  const scopedTpIdsKey = scopedTpIds.join(',');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchPriceChangeLogs({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        search: hideFilters ? '' : search,
        source: hideFilters ? '' : source,
        pricingMode: hideFilters ? undefined : clientType || undefined,
        syncStatus: syncStatus || undefined,
        tpIds: scopedTpIds.length ? scopedTpIds : undefined,
      });
      setLogs(result.logs || []);
      setTotalCount(result.totalCount || 0);
    } catch (err: any) {
      setError(err?.message || 'Failed to load price change logs');
      setLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, source, clientType, syncStatus, hideFilters, scopedTpIds, scopedTpIdsKey]);

  useEffect(() => {
    void load();
  }, [load, refreshSignal]);

  useEffect(() => {
    setPage(0);
  }, [scopedTpIdsKey]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const rangeLabel = useMemo(() => {
    if (!totalCount) return '0 logs';
    const from = page * PAGE_SIZE + 1;
    const to = Math.min(totalCount, (page + 1) * PAGE_SIZE);
    return `${from}–${to} of ${totalCount}`;
  }, [page, totalCount]);

  return (
    <div className={compact ? styles.panelCompact : undefined}>
      {!hideFilters && (
        <div className={styles.toolbar}>
          <input
            className={styles.searchInput}
            placeholder="Search client, email, event, section, changer…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(0);
                setSearch(searchInput.trim());
              }
            }}
          />
          <select
            className={styles.selectInput}
            value={source}
            onChange={(e) => {
              setPage(0);
              setSource(e.target.value);
            }}
          >
            {SOURCE_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            className={styles.selectInput}
            value={clientType}
            onChange={(e) => {
              setPage(0);
              setClientType(e.target.value);
            }}
          >
            {CLIENT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value || 'all-types'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            className={styles.selectInput}
            value={syncStatus}
            aria-label="Filter by status"
            onChange={(e) => {
              setPage(0);
              setSyncStatus(e.target.value);
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all-statuses'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={styles.refreshBtn}
            disabled={loading}
            onClick={() => {
              setPage(0);
              setSearch(searchInput.trim());
              void load();
            }}
          >
            <RefreshRoundedIcon fontSize="small" />
            Refresh
          </button>
        </div>
      )}

      {hideFilters && (
        <div className={styles.toolbar}>
          <select
            className={styles.selectInput}
            value={syncStatus}
            aria-label="Filter by status"
            onChange={(e) => {
              setPage(0);
              setSyncStatus(e.target.value);
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all-statuses'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={styles.refreshBtn}
            disabled={loading}
            onClick={() => void load()}
          >
            <RefreshRoundedIcon fontSize="small" />
            Refresh
          </button>
        </div>
      )}

      <div className={styles.card}>
        {loading ? (
          <div className={styles.loading}>Loading price change logs…</div>
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : logs.length === 0 ? (
          <div className={styles.empty}>No price changes found.</div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Source</th>
                  <th>Changed by</th>
                  {!hideClientColumns && <th>Client</th>}
                  {!hideClientColumns && <th>Client type</th>}
                  <th>Event / Seat</th>
                  <th>Old → New</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <div className={styles.stack}>
                        <span>{formatWhen(row.createdAt)}</span>
                        {row.tpStatus ? (
                          <span className={styles.muted}>{row.tpStatus}</span>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <span className={`${styles.badge} ${sourceBadgeClass(row.sourceKey)}`}>
                        {row.sourceLabel}
                      </span>
                    </td>
                    <td>
                      <div className={styles.stack}>
                        <span>{changedByFallback(row)}</span>
                        <span className={styles.muted}>
                          {isVlAutoSyncSource(row.sourceKey)
                            ? '—'
                            : row.changedByEmail || '—'}
                        </span>
                        {!isVlAutoSyncSource(row.sourceKey) && row.changedByRole ? (
                          <span className={styles.muted}>{row.changedByRole}</span>
                        ) : null}
                      </div>
                    </td>
                    {!hideClientColumns && (
                      <td>
                        <div className={styles.stack}>
                          <span>{row.clientName || '—'}</span>
                          <span className={styles.muted}>{row.clientEmail || '—'}</span>
                        </div>
                      </td>
                    )}
                    {!hideClientColumns && (
                      <td>
                        <span className={`${styles.badge} ${clientTypeBadgeClass(row.clientPricingMode)}`}>
                          {formatClientTypeLabel(row.clientPricingMode)}
                        </span>
                      </td>
                    )}
                    <td>
                      <div className={styles.stack}>
                        <span>{row.eventName || '—'}</span>
                        <span className={styles.muted}>
                          {[row.section && `Sec ${row.section}`, row.row && `Row ${row.row}`, row.seat != null && `Seat ${row.seat}`]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </span>
                        {row.venue ? <span className={styles.muted}>{row.venue}</span> : null}
                      </div>
                    </td>
                    <td>
                      <div className={`${styles.stack} ${priceClass(row.direction)}`}>
                        <span>
                          {formatMoney(row.oldPrice)} → {formatMoney(row.newPrice)}
                          {row.direction === 'up' ? (
                            <TrendingUpIcon style={{ fontSize: 14, marginLeft: 4, verticalAlign: 'middle' }} />
                          ) : null}
                          {row.direction === 'down' ? (
                            <TrendingDownIcon style={{ fontSize: 14, marginLeft: 4, verticalAlign: 'middle' }} />
                          ) : null}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.stack}>
                        <span className={`${styles.badge} ${syncStatusBadgeClass(row.syncStatus)}`}>
                          {String(row.syncStatus || '').toLowerCase() === 'failure' ? 'Failure' : 'Success'}
                        </span>
                        {String(row.syncStatus || '').toLowerCase() === 'failure' && row.systemsNote ? (
                          <span className={styles.muted}>{row.systemsNote}</span>
                        ) : null}
                        {String(row.syncStatus || '').toLowerCase() === 'failure' && row.failureReason ? (
                          <span className={styles.muted}>{row.failureReason}</span>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className={styles.footer}>
          <span className={styles.muted}>{rangeLabel}</span>
          <div className={styles.pager}>
            <button
              type="button"
              disabled={loading || page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <span className={styles.muted}>
              Page {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              disabled={loading || page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceChangeLogsPanel;
